/**
 * Derived metrics + formatters shared across every table that shows trade /
 * position data. Keep this the single source of truth so the Paper Trading,
 * Journal, Wallet, and Chart-side panels all agree on what a number means.
 */

export type TradeSide = 'long' | 'short';

export type MarketType = 'SPOT' | 'MARGIN' | 'FUTURES';

/**
 * Privacy ("streamer") mode — when on, every USD value rendered through the
 * shared formatters below collapses to a fixed mask so the operator can stream
 * / screenshot the app without leaking balances, P&L, or order sizes.
 *
 * This is a module-level flag (not a React hook) on purpose: the formatters are
 * plain functions called from non-component code paths too, and we want a
 * single source of truth that masks "no matter where you are". The settings
 * store keeps this in sync via setPrivacyMode(); see settingsStore.ts.
 *
 * Components still re-render on the toggle because they subscribe to the store
 * value; this flag is what the pure formatters read at call time.
 */
let privacyMaskEnabled = false;

/** The literal mask shown in place of any USD value when privacy mode is on. */
export const USD_MASK = '*******';

/** Sync the module flag. Called by the settings store on hydrate + toggle. */
export function setPrivacyMask(on: boolean): void {
  privacyMaskEnabled = on;
}

export function isPrivacyMask(): boolean {
  return privacyMaskEnabled;
}

/**
 * THE single USD formatter for plain dollar amounts. Routes every ad-hoc
 * `$${v.toLocaleString(...)}` call site through one place so privacy mode is
 * total. Returns the mask when privacy mode is on; otherwise `$1,234.56`.
 *
 * `prefix` lets callers render e.g. `C$` for CAD while still masking.
 */
export function formatUsd(
  v: number | null | undefined,
  opts?: { prefix?: string; minimumFractionDigits?: number; maximumFractionDigits?: number },
): string {
  if (privacyMaskEnabled) return USD_MASK;
  if (v == null || Number.isNaN(v)) return '--';
  const prefix = opts?.prefix ?? '$';
  return `${prefix}${v.toLocaleString(undefined, {
    minimumFractionDigits: opts?.minimumFractionDigits ?? 2,
    maximumFractionDigits: opts?.maximumFractionDigits ?? 2,
  })}`;
}

/**
 * Format a number with decimals that scale with magnitude. Use this for all
 * price/qty/notional display so sub-cent tokens like PEPE still show
 * distinguishable values.
 */
export function formatAmount(n: number | null | undefined, decimals?: number): string {
  if (n == null || Number.isNaN(n)) return '--';
  if (decimals !== undefined) {
    return n.toLocaleString(undefined, {
      minimumFractionDigits: decimals,
      maximumFractionDigits: decimals,
    });
  }
  const abs = Math.abs(n);
  const d = abs >= 100 ? 2 : abs >= 1 ? 4 : abs >= 0.01 ? 6 : 8;
  return n.toLocaleString(undefined, {
    minimumFractionDigits: d,
    maximumFractionDigits: d,
  });
}

/**
 * Format a USD notional like `$1.23M`, `$4.56K`, `$0.12`. Keeps big numbers
 * readable without losing precision on small ones.
 */
export function formatNotional(n: number | null | undefined): string {
  if (privacyMaskEnabled) return USD_MASK;
  if (n == null || Number.isNaN(n)) return '--';
  const abs = Math.abs(n);
  const sign = n < 0 ? '-' : '';
  if (abs >= 1_000_000_000) return `${sign}$${(abs / 1_000_000_000).toFixed(2)}B`;
  if (abs >= 1_000_000) return `${sign}$${(abs / 1_000_000).toFixed(2)}M`;
  if (abs >= 10_000) return `${sign}$${(abs / 1_000).toFixed(2)}K`;
  if (abs >= 1) return `${sign}$${abs.toFixed(2)}`;
  return `${sign}$${formatAmount(abs)}`;
}

/** `+1.23%` / `-0.45%` / `0.00%`. Handles null. */
export function formatPctSigned(pct: number | null | undefined, decimals = 2): string {
  if (pct == null || Number.isNaN(pct)) return '--';
  const sign = pct > 0 ? '+' : '';
  return `${sign}${pct.toFixed(decimals)}%`;
}

/** `+1h 23m`, `45m`, `3d 2h`. Takes a ms duration. */
export function formatDuration(ms: number | null | undefined): string {
  if (ms == null || Number.isNaN(ms) || ms < 0) return '--';
  const s = Math.floor(ms / 1000);
  if (s < 60) return `${s}s`;
  const m = Math.floor(s / 60);
  if (m < 60) return `${m}m`;
  const h = Math.floor(m / 60);
  const rm = m % 60;
  if (h < 24) return rm > 0 ? `${h}h ${rm}m` : `${h}h`;
  const d = Math.floor(h / 24);
  const rh = h % 24;
  return rh > 0 ? `${d}d ${rh}h` : `${d}d`;
}

export type PositionInput = {
  side: TradeSide | string;
  entryPrice: number;
  currentPrice: number;
  positionQty?: number | undefined;
  positionSize?: number | undefined;
  openedAt?: number | undefined;
  stopLossPrice?: number | undefined;
  takeProfitPrice?: number | undefined;
};

export type PositionMetrics = {
  /** Base-asset quantity (e.g. BTC). Null when the engine didn't publish it. */
  qty: number | null;
  /** Notional at entry (qty × entry). */
  entryValue: number | null;
  /** Notional at current mark (qty × current). */
  currentValue: number | null;
  /** Unrealized P&L in quote (USD). */
  unrealizedPnl: number | null;
  /** Unrealized P&L as % of entry notional. */
  unrealizedPnlPct: number | null;
  /** Milliseconds since openedAt. */
  durationMs: number | null;
  /** Distance to SL as a signed % of current price (negative = SL below). */
  distanceToSlPct: number | null;
  /** Distance to TP as a signed % of current price (positive = TP above). */
  distanceToTpPct: number | null;
  /** Initial risk = qty × |entry − SL|. Null if SL missing. */
  initialRisk: number | null;
  /** R-multiple = unrealizedPnl / initialRisk. */
  rMultiple: number | null;
};

/**
 * Compute derived per-position metrics. All inputs are optional so this is
 * safe to call with partial data — missing values propagate as null rather
 * than NaN.
 */
export function computePositionMetrics(pos: PositionInput): PositionMetrics {
  const side: TradeSide = pos.side === 'short' ? 'short' : 'long';
  const qty = pos.positionQty ?? null;
  const entryValue =
    pos.positionSize ?? (qty != null ? qty * pos.entryPrice : null);
  const currentValue = qty != null ? qty * pos.currentPrice : null;

  const unrealizedPnl =
    qty != null
      ? side === 'long'
        ? (pos.currentPrice - pos.entryPrice) * qty
        : (pos.entryPrice - pos.currentPrice) * qty
      : null;

  const unrealizedPnlPct =
    entryValue != null && entryValue !== 0 && unrealizedPnl != null
      ? (unrealizedPnl / entryValue) * 100
      : null;

  const durationMs = pos.openedAt != null ? Date.now() - pos.openedAt : null;

  const sl = pos.stopLossPrice;
  const tp = pos.takeProfitPrice;
  const cur = pos.currentPrice;

  const distanceToSlPct =
    sl != null && cur > 0 ? ((sl - cur) / cur) * 100 : null;
  const distanceToTpPct =
    tp != null && cur > 0 ? ((tp - cur) / cur) * 100 : null;

  const initialRisk =
    sl != null && qty != null ? Math.abs(pos.entryPrice - sl) * qty : null;

  const rMultiple =
    initialRisk != null && initialRisk !== 0 && unrealizedPnl != null
      ? unrealizedPnl / initialRisk
      : null;

  return {
    qty,
    entryValue,
    currentValue,
    unrealizedPnl,
    unrealizedPnlPct,
    durationMs,
    distanceToSlPct,
    distanceToTpPct,
    initialRisk,
    rMultiple,
  };
}
